import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronDownIcon,
  CircleAlertIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react';

import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import {
  fsboBlockedErrorCopy,
  isFsboBlockedError,
} from '@modules/_shared/utils/fsboBlockedError';
import { useLocale } from '@shared/hooks/locale';

type PortalPublicationErrorDialogProps = {
  error: ErrorLike | null;
  onClose: () => void;
  onRetryWithoutFTPCheck?: () => Promise<any>;
  loading?: boolean;
};

export const PortalPublicationErrorDialog = ({
  error,
  onClose,
  onRetryWithoutFTPCheck,
  loading,
}: PortalPublicationErrorDialogProps) => {
  const { t } = useLocale();
  const [showDetails, setShowDetails] = useState(false);
  // A blocked FSBO advertiser (#24256): one sentence and what to do, no
  // raw payload — there is nothing to retry.
  const blocked = isFsboBlockedError(error);
  const blockedCopy = fsboBlockedErrorCopy(t);

  const extensionErrors = useMemo(() => {
    const graphQLError = CombinedGraphQLErrors.is(error)
      ? error.errors[0]
      : undefined;

    if (graphQLError == null || !Array.isArray(graphQLError.extensions)) {
      return [];
    }

    // We filter out the raw error because it's not a user-friendly error.
    return graphQLError.extensions.reduce<
      Array<{
        field: string;
        message: string;
        isFtpConnectionError: boolean;
      }>
    >((acc, error) => {
      if (error.raw == null) {
        acc.push({
          // In case something happen during the prettification, we use the original field name.
          field: error.field ?? error.path?.join('.'),
          message: error.message,
          isFtpConnectionError: error.code === 'FTP_CONNECTION_ERROR',
        });
      }

      return acc;
    }, []);
  }, [error]);

  const rawError = useMemo(() => {
    const graphQLError = CombinedGraphQLErrors.is(error)
      ? error.errors[0]
      : undefined;
    if (graphQLError == null || !Array.isArray(graphQLError.extensions)) {
      return error;
    }

    const rawError = graphQLError.extensions.find(
      error => error.raw != null,
    )?.raw;

    return rawError ?? error;
  }, [error]);

  const isFtpConnectionError = useMemo(() => {
    return extensionErrors.some(error => error.isFtpConnectionError);
  }, [extensionErrors]);

  const onSave = useMemo(() => {
    if (onRetryWithoutFTPCheck != null && isFtpConnectionError) {
      return () => {
        // We don't need to catch here because error is handled at the mutation level. Rejection is only used to avoid closing the dialog prematurely.
        onRetryWithoutFTPCheck().then(() => {
          onClose();
        });
      };
    }

    return undefined;
  }, [onRetryWithoutFTPCheck, isFtpConnectionError, onClose]);

  return (
    <SlideDialog
      open={error != null}
      onClose={onClose}
      title={
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <CircleAlertIcon size={18} color={'var(--mui-palette-error-main)'} />
          {t('Publication error')}
        </Stack>
      }
      maxWidth="sm"
      fullWidth
      cancelText={t('Close')}
      onSave={onSave}
      loading={loading}
      saveText={t('Publish anyway')}
      saveIcon={<UploadIcon />}
      dialogProps={{
        scroll: 'paper',
      }}
    >
      <Box
        sx={{
          py: 2,
          px: 4,
        }}
      >
        {blocked ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>{blockedCopy.title}</AlertTitle>
            {blockedCopy.body}
          </Alert>
        ) : (
          <Typography variant="body1" sx={{ mb: 3 }}>
            {error?.message}
          </Typography>
        )}
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {extensionErrors.map((err, i) => (
            <Stack
              direction="row"
              key={i}
              sx={{
                gap: 1,
              }}
            >
              <XIcon size={18} color={'var(--mui-palette-error-main)'} />
              <Box sx={{ mt: '-2px' }}>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  {err.field}
                </Typography>
                <Typography variant="body2">{err.message}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
        {rawError != null && !blocked && (
          <Box sx={{ mt: 3 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowDetails(prev => !prev)}
            >
              {showDetails ? t('Hide details') : t('Show details')}
              <ChevronDownIcon
                size={18}
                style={{
                  transition: 'transform 0.2s ease-in-out',
                  transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Button>
            <Collapse in={showDetails}>
              <Box
                sx={{
                  p: 1,
                  width: '100%',
                  overflow: 'auto',
                }}
              >
                <pre style={{ marginTop: 0 }}>
                  {JSON.stringify(rawError, null, 2)}
                </pre>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </SlideDialog>
  );
};
